// src/utils/i18n.ts
// A small string table for the learning flow, keyed by the learner's own
// language (the course's nativeLang). Every course is taught in English except
// English itself, which is taught to Spanish speakers, so only "en" and "es"
// exist. Only what a learner touches every day is here: the lesson runner,
// the feedback sheet, quick study, the hatch questions intro and the Learn
// tab's continue and course cards. The rest of the app stays English.
import { useMemo } from "react";
import { useUser } from "../Context/UserContext";

export type UiLang = "en" | "es";

const EN = {
  // Lesson runner
  "lesson.check": "Check",
  "lesson.continue": "Continue",
  "lesson.instruction.translate": "Translate to English",
  "lesson.instruction.build": "Build the sentence",
  "lesson.instruction.mcq": "Pick the right answer",
  "lesson.instruction.text": "Type the answer",
  "lesson.instruction.blank": "Fill in the blank",
  "lesson.instruction.match": "Match the pairs",
  "lesson.instruction.order": "Put them in order",
  "lesson.instruction.other": "Answer the question",
  "lesson.retry": "Let's try that one again",
  "lesson.hint": "Hint",
  "lesson.needHint": "Need a hint?",
  "lesson.hintUsed": "Hint used",
  "lesson.playAudio": "Play audio",
  "lesson.empty": "No questions available in this lesson yet.",
  "lesson.close": "Close lesson",
  "lesson.typePlaceholder": "Type your answer",
  "lesson.blankLabel": "Fill in the blank",
  "lesson.correct": "Correct!",
  "lesson.incorrect": "Incorrect",
  "lesson.notQuite": "Not quite",
  "lesson.yourSentence": "Your sentence",
  "lesson.wordBank": "Word bank",
  "lesson.answerOptions": "Answer options",
  "lesson.matchTry": "Try {left} with {right}",
  "lesson.startsWith": "The answer starts with",
  "lesson.goesFirst": "goes first",
  "lesson.matched": "{n} of {total} matched",
  "lesson.matchTryWord": "Try",
  "lesson.matchWith": "with",
  "lesson.quit.title": "Quit lesson?",
  "lesson.quit.text": "Your progress in this unit won't be saved.",
  "lesson.quit.quit": "Quit",
  "lesson.quit.keep": "Keep going",
  "lesson.quit.done": "{n} of {total} done",
  // Feedback sheet
  "feedback.nice": "Nice!",
  "feedback.notQuite": "Not quite",
  "feedback.answer": "Correct answer",
  "feedback.stardust": "+{n} stardust",
  "feedback.again": "You'll get another go at the end of the lesson.",
  // End of unit card
  "unit.lesson": "Lesson",
  "unit.complete": "Lesson complete!",
  "unit.continue": "Continue",
  "unit.next": "Start the next lesson",
  "unit.stars": "Stars earned",
  "unit.accuracy": "Accuracy",
  "unit.collected": "Collected",
  // Quick study
  "qs.eyebrow": "Quick study",
  "qs.noQuestions": "No questions yet",
  "qs.noQuestionsText": "Your course hasn't loaded any questions yet. Open Learn to start a lesson.",
  "qs.goLearn": "Go to Learn",
  "qs.perfect": "Perfect round!",
  "qs.niceWork": "Nice work!",
  "qs.keepPractising": "Keep practising!",
  "qs.fullLesson": "Full lesson",
  "qs.backToPlay": "Back to play",
  "qs.starsToSpend": "stars to spend in Play",
  "qs.score": "{n} of {total} correct",
  "qs.each": "+{n} each",
  "qs.continue": "Continue",
  "qs.seeStars": "See my stars",
  "qs.question": "Question",
  "qs.kind.choose": "Choose",
  "qs.kind.blank": "Fill the blank",
  "qs.kind.translate": "Translate",
  "qs.kind.meaning": "Meaning",
  "qs.listen": "Listen",
  "qs.correct": "Correct!",
  "qs.starsOnWay": "+{n} stars on their way",
  "qs.notQuite": "Not quite.",
  "qs.answerIs": "The answer is",
  // Hatch questions intro
  "hatch.found": "Three eggs found you",
  "hatch.which": "Which egg is calling to you?",
  "hatch.lead": "Answer three questions to keep it warm, and your first Luna will hatch.",
  "hatch.keep": "Keep this egg",
  "hatch.loading": "Getting your questions ready...",
  "hatch.question": "Question {n} of {total}",
  "hatch.oneMore": "One more try",
  "hatch.gotIt": "Got it this time!",
  "hatch.correct": "Correct! +{n} stars.",
  "hatch.notQuite": "Not quite. This one comes back in a moment.",
  "hatch.hatch": "Hatch!",
  "hatch.next": "Next",
  "hatch.hatching": "It's hatching...",
  "hatch.sayHi": "Say hi to",
  "hatch.first": "{name} is the first Luna in your collection. Keep learning to collect the rest.",
  "hatch.saving": "Saving...",
  "hatch.startPlaying": "Start playing",
  "hatch.stars": "stars",
  "hatch.egg.comet": "Comet egg",
  "hatch.egg.comet.hint": "Brave and bouncy",
  "hatch.egg.lunar": "Lunar egg",
  "hatch.egg.lunar.hint": "Calm and clever",
  "hatch.egg.solar": "Solar egg",
  "hatch.egg.solar.hint": "Sunny and gentle",
  "hatch.egg.cosmic": "Cosmic egg",
  "hatch.egg.cosmic.hint": "Full of wonder",
  "hatch.egg.mystery": "Mystery egg",
  "hatch.egg.mystery.hint": "Full of surprises",
  // Learn tab
  "learn.startHere": "Start here",
  "learn.upNext": "Up next",
  "learn.unit": "Unit {n}: {title}",
  "learn.stars": "{n} stars",
  "learn.start": "Start",
  "learn.continue": "Continue",
  "learn.loading": "Loading…",
  "learn.review": "Review {n} due",
  "learn.allCaught": "All caught up",
  "learn.finished": "You've finished every unit!",
  "learn.finishedSub": "Practice with a role-play, or replay any course below.",
  "learn.browse": "Browse courses",
  "course.playAgain": "Play again",
  "course.continue": "Continue",
  "course.start": "Start Learning",
  "course.units": "{done}/{total} units",
  // Sign-up About-you step (only shown in Spanish for the English course)
  "signup.levelQ": "How much English do you know?",
  "signup.level.new": "Brand new",
  "signup.level.new.note": "Starting from zero",
  "signup.level.basics": "Know some words",
  "signup.level.basics.note": "Hello, thank you, numbers",
  "signup.level.intermediate": "Simple sentences",
  "signup.level.intermediate.note": "Can order food and ask the way",
  "signup.level.conversational": "Conversations",
  "signup.level.conversational.note": "Can chat about everyday things",
  "signup.level.advanced": "Pretty fluent",
  "signup.level.advanced.note": "Want to polish and sound natural",
  "signup.goalQ": "Why are you learning?",
  "signup.goal.travel": "Travel",
  "signup.goal.travel.note": "Order food, ask directions, chat with locals",
  "signup.goal.partner": "My partner",
  "signup.goal.partner.note": "Talk with them and their family",
  "signup.goal.family": "Family and friends",
  "signup.goal.family.note": "Talk with people I love",
  "signup.goal.work": "Work",
  "signup.goal.work.note": "Meetings, emails and customers",
  "signup.goal.moving": "Moving abroad",
  "signup.goal.moving.note": "Settle in and feel at home",
  "signup.goal.citizenship": "Citizenship test",
  "signup.goal.citizenship.note": "Residency, citizenship or a language exam",
  "signup.goal.school": "School",
  "signup.goal.school.note": "Homework, tests and class",
  "signup.goal.culture": "Music, shows and books",
  "signup.goal.culture.note": "Enjoy it without subtitles",
  "signup.goal.fun": "For fun",
  "signup.goal.fun.note": "Just because I want to",
  "signup.dailyQ": "How much time each day?",
  "signup.dailyHint": "You can change this any time.",
  "signup.daily.5": "Casual",
  "signup.daily.10": "Regular",
  "signup.daily.15": "Serious",
  "signup.aboutStep": "About you · {n} of 3",
  "signup.makeItYours": "Make it yours",
  "signup.create": "Create account",
  "signup.skip": "Skip for now",
  "luna.grew": "Your Luna grew",
  "luna.levelShort": "Lv {n}",
  "luna.levelUp": "Level {from} to {to}",
  "luna.rest": "Rest",
  "luna.evolution": "Evolution",
  "luna.evolving": "{name} is evolving",
  "luna.newInCollection": "New in your collection",
  "luna.seeCollection": "See collection",
  "extra.fix.tag": "Fix the sentence",
  "extra.fix.find": "Tap the word that's wrong",
  "extra.fix.pick": "Pick the word that fixes it",
  "extra.fix.done": "Fixed sentence",
  "extra.fix.notThat": "Not that one. The wrong word is in red.",
  "extra.fix.swap": "Swap",
  "extra.fix.swapFor": "for",
  "extra.fix.right": "Fixed it!",
  "extra.fix.shouldBe": "should be",
  "extra.listen.tag": "Listen and pick",
  "extra.listen.eyebrow": "Tap what you hear",
  "extra.listen.play": "Play the sentence",
  "extra.listen.right": "Great ear!",
  "extra.speed.tag": "Speed round",
  "extra.speed.sec": "sec",
  "extra.speed.title": "{n} quick ones",
  "extra.speed.intro": "Tap the answer, no Check needed. Get all {n} in time for a stardust bonus.",
  "extra.speed.timeUp": "Time's up",
  "extra.speed.seconds": "{n} seconds",
  "extra.speed.clean": "Clean sweep!",
  "extra.speed.late": "All {n}, just past the clock",
  "extra.speed.score": "Speed round: {right} of {n}",
  "extra.speed.bonus": "Speed bonus",
  "extra.speed.cleanNote": "{right} right in {s} seconds.",
  "extra.speed.lateNote": "Speed comes from seeing words often. These ones will come back.",
  "extra.start": "Start",
  "luna.swapEyebrow": "Your Luna",
  "luna.swapTitle": "Power up a Luna",
  "luna.swapLead": "Right answers give stardust to the Luna you pick.",
  "luna.equipped": "Powering up",
  "luna.swapFailed": "Couldn't swap your Luna",
  "luna.tapToSwap": "Tap to swap Luna",
  "luna.levelUpBadge": "Level up",
};
export type I18nKey = keyof typeof EN;

const ES: Record<I18nKey, string> = {
  "lesson.check": "Comprobar",
  "lesson.continue": "Continuar",
  "lesson.instruction.translate": "Traduce al español",
  "lesson.instruction.build": "Arma la frase en inglés",
  "lesson.instruction.mcq": "Elige la respuesta correcta",
  "lesson.instruction.text": "Escribe la respuesta",
  "lesson.instruction.blank": "Completa el espacio",
  "lesson.instruction.match": "Une las parejas",
  "lesson.instruction.order": "Ponlos en orden",
  "lesson.instruction.other": "Responde la pregunta",
  "lesson.retry": "Intentemos esa otra vez",
  "lesson.hint": "Pista",
  "lesson.needHint": "¿Necesitas una pista?",
  "lesson.hintUsed": "Pista usada",
  "lesson.playAudio": "Escuchar",
  "lesson.empty": "Esta lección todavía no tiene preguntas.",
  "lesson.close": "Cerrar la lección",
  "lesson.typePlaceholder": "Escribe tu respuesta",
  "lesson.blankLabel": "Completa el espacio",
  "lesson.correct": "¡Correcto!",
  "lesson.incorrect": "Incorrecto",
  "lesson.notQuite": "Casi",
  "lesson.yourSentence": "Tu frase",
  "lesson.wordBank": "Banco de palabras",
  "lesson.answerOptions": "Opciones de respuesta",
  "lesson.matchTry": "Prueba {left} con {right}",
  "lesson.startsWith": "La respuesta empieza con",
  "lesson.goesFirst": "va primero",
  "lesson.matched": "{n} de {total} unidas",
  "lesson.matchTryWord": "Prueba",
  "lesson.matchWith": "con",
  "lesson.quit.title": "¿Salir de la lección?",
  "lesson.quit.text": "No se guardará tu progreso en esta unidad.",
  "lesson.quit.quit": "Salir",
  "lesson.quit.keep": "Seguir",
  "lesson.quit.done": "{n} de {total} hechas",
  "feedback.nice": "¡Muy bien!",
  "feedback.notQuite": "Casi",
  "feedback.answer": "Respuesta correcta",
  "feedback.stardust": "+{n} polvo estelar",
  "feedback.again": "La verás otra vez al final de la lección.",
  "unit.lesson": "Lección",
  "unit.complete": "¡Lección completada!",
  "unit.continue": "Continuar",
  "unit.next": "Empezar la siguiente lección",
  "unit.stars": "Estrellas ganadas",
  "unit.accuracy": "Precisión",
  "unit.collected": "Recogidas",
  "qs.eyebrow": "Repaso rápido",
  "qs.noQuestions": "Todavía no hay preguntas",
  "qs.noQuestionsText": "Tu curso todavía no tiene preguntas. Abre Aprender para empezar una lección.",
  "qs.goLearn": "Ir a Aprender",
  "qs.perfect": "¡Ronda perfecta!",
  "qs.niceWork": "¡Buen trabajo!",
  "qs.keepPractising": "¡Sigue practicando!",
  "qs.fullLesson": "Lección completa",
  "qs.backToPlay": "Volver a jugar",
  "qs.starsToSpend": "estrellas para gastar en Jugar",
  "qs.score": "{n} de {total} correctas",
  "qs.each": "+{n} cada una",
  "qs.continue": "Continuar",
  "qs.seeStars": "Ver mis estrellas",
  "qs.question": "Pregunta",
  "qs.kind.choose": "Elige",
  "qs.kind.blank": "Completa",
  "qs.kind.translate": "Traduce",
  "qs.kind.meaning": "Significado",
  "qs.listen": "Escuchar",
  "qs.correct": "¡Correcto!",
  "qs.starsOnWay": "+{n} estrellas en camino",
  "qs.notQuite": "Casi.",
  "qs.answerIs": "La respuesta es",
  "hatch.found": "Tres huevos te encontraron",
  "hatch.which": "¿Qué huevo te está llamando?",
  "hatch.lead": "Responde tres preguntas para mantenerlo calentito y tu primera Luna nacerá.",
  "hatch.keep": "Me quedo con este huevo",
  "hatch.loading": "Preparando tus preguntas...",
  "hatch.question": "Pregunta {n} de {total}",
  "hatch.oneMore": "Un intento más",
  "hatch.gotIt": "¡Esta vez sí!",
  "hatch.correct": "¡Correcto! +{n} estrellas.",
  "hatch.notQuite": "Casi. Esta pregunta vuelve en un momento.",
  "hatch.hatch": "¡A nacer!",
  "hatch.next": "Siguiente",
  "hatch.hatching": "Está naciendo...",
  "hatch.sayHi": "Saluda a",
  "hatch.first": "{name} es la primera Luna de tu colección. Sigue aprendiendo para reunir las demás.",
  "hatch.saving": "Guardando...",
  "hatch.startPlaying": "Empezar a jugar",
  "hatch.stars": "estrellas",
  "hatch.egg.comet": "Huevo cometa",
  "hatch.egg.comet.hint": "Valiente y saltarín",
  "hatch.egg.lunar": "Huevo lunar",
  "hatch.egg.lunar.hint": "Tranquilo y listo",
  "hatch.egg.solar": "Huevo solar",
  "hatch.egg.solar.hint": "Alegre y tierno",
  "hatch.egg.cosmic": "Huevo cósmico",
  "hatch.egg.cosmic.hint": "Lleno de asombro",
  "hatch.egg.mystery": "Huevo misterioso",
  "hatch.egg.mystery.hint": "Lleno de sorpresas",
  "learn.startHere": "Empieza aquí",
  "learn.upNext": "A continuación",
  "learn.unit": "Unidad {n}: {title}",
  "learn.stars": "{n} estrellas",
  "learn.start": "Empezar",
  "learn.continue": "Continuar",
  "learn.loading": "Cargando…",
  "learn.review": "Repasar {n} pendientes",
  "learn.allCaught": "Todo al día",
  "learn.finished": "¡Terminaste todas las unidades!",
  "learn.finishedSub": "Practica con un juego de rol o repite cualquier curso.",
  "learn.browse": "Ver cursos",
  "course.playAgain": "Jugar otra vez",
  "course.continue": "Continuar",
  "course.start": "Empezar a aprender",
  "course.units": "{done}/{total} unidades",
  "signup.levelQ": "¿Cuánto inglés sabes?",
  "signup.level.new": "Desde cero",
  "signup.level.new.note": "Empiezo desde el principio",
  "signup.level.basics": "Sé algunas palabras",
  "signup.level.basics.note": "Hello, thank you, los números",
  "signup.level.intermediate": "Frases sencillas",
  "signup.level.intermediate.note": "Puedo pedir comida y preguntar el camino",
  "signup.level.conversational": "Conversaciones",
  "signup.level.conversational.note": "Puedo hablar de cosas del día a día",
  "signup.level.advanced": "Bastante fluido",
  "signup.level.advanced.note": "Quiero pulirlo y sonar natural",
  "signup.goalQ": "¿Por qué quieres aprender?",
  "signup.goal.travel": "Viajar",
  "signup.goal.travel.note": "Pedir comida, preguntar direcciones, hablar con la gente",
  "signup.goal.partner": "Mi pareja",
  "signup.goal.partner.note": "Hablar con mi pareja y su familia",
  "signup.goal.family": "Familia y amigos",
  "signup.goal.family.note": "Hablar con la gente que quiero",
  "signup.goal.work": "Trabajo",
  "signup.goal.work.note": "Reuniones, correos y clientes",
  "signup.goal.moving": "Mudarme",
  "signup.goal.moving.note": "Instalarme y sentirme en casa",
  "signup.goal.citizenship": "Examen de ciudadanía",
  "signup.goal.citizenship.note": "Residencia, ciudadanía o un examen de idioma",
  "signup.goal.school": "La escuela",
  "signup.goal.school.note": "Tareas, exámenes y clases",
  "signup.goal.culture": "Música, series y libros",
  "signup.goal.culture.note": "Disfrutarlos sin subtítulos",
  "signup.goal.fun": "Por diversión",
  "signup.goal.fun.note": "Porque me apetece",
  "signup.dailyQ": "¿Cuánto tiempo al día?",
  "signup.dailyHint": "Puedes cambiarlo cuando quieras.",
  "signup.daily.5": "Tranquilo",
  "signup.daily.10": "Regular",
  "signup.daily.15": "Intenso",
  "signup.aboutStep": "Sobre ti · {n} de 3",
  "signup.makeItYours": "Hazlo tuyo",
  "signup.create": "Crear cuenta",
  "signup.skip": "Omitir por ahora",
  "luna.grew": "Tu Luna creció",
  "luna.levelShort": "Nv {n}",
  "luna.levelUp": "Nivel {from} a {to}",
  "luna.rest": "Descansa",
  "luna.evolution": "Evolución",
  "luna.evolving": "{name} está evolucionando",
  "luna.newInCollection": "Nuevo en tu colección",
  "luna.seeCollection": "Ver colección",
  "extra.fix.tag": "Corrige la frase",
  "extra.fix.find": "Toca la palabra incorrecta",
  "extra.fix.pick": "Elige la palabra correcta",
  "extra.fix.done": "Frase corregida",
  "extra.fix.notThat": "Esa no. La palabra incorrecta está en rojo.",
  "extra.fix.swap": "Cambia",
  "extra.fix.swapFor": "por",
  "extra.fix.right": "¡Corregida!",
  "extra.fix.shouldBe": "debería ser",
  "extra.listen.tag": "Escucha y elige",
  "extra.listen.eyebrow": "Toca lo que oyes",
  "extra.listen.play": "Escuchar la frase",
  "extra.listen.right": "¡Buen oído!",
  "extra.speed.tag": "Ronda rápida",
  "extra.speed.sec": "seg",
  "extra.speed.title": "{n} rapiditas",
  "extra.speed.intro": "Toca la respuesta, sin Comprobar. Acierta las {n} a tiempo y ganas polvo estelar extra.",
  "extra.speed.timeUp": "Se acabó el tiempo",
  "extra.speed.seconds": "{n} segundos",
  "extra.speed.clean": "¡Pleno!",
  "extra.speed.late": "Las {n}, justo después del tiempo",
  "extra.speed.score": "Ronda rápida: {right} de {n}",
  "extra.speed.bonus": "Bonus de velocidad",
  "extra.speed.cleanNote": "{right} bien en {s} segundos.",
  "extra.speed.lateNote": "La rapidez llega viendo las palabras a menudo. Estas volverán.",
  "extra.start": "Empezar",
  "luna.swapEyebrow": "Tu Luna",
  "luna.swapTitle": "Elige una Luna",
  "luna.swapLead": "Tus respuestas correctas dan polvo estelar a la Luna que elijas.",
  "luna.equipped": "Recibiendo polvo",
  "luna.swapFailed": "No se pudo cambiar tu Luna",
  "luna.tapToSwap": "Toca para cambiar de Luna",
  "luna.levelUpBadge": "¡Sube de nivel!",
};
const TABLES: Record<UiLang, Record<I18nKey, string>> = { en: EN, es: ES };

/** Every key, for the unit test that checks both tables. */
export const __test_keys: Readonly<Record<I18nKey, string>> = EN;

/** "es", "es-MX", "ES" -> "es"; anything without a table -> "en". */
export function uiLangOf(nativeLang?: string | null): UiLang {
  const code = String(nativeLang || "").trim().toLowerCase().split(/[-_]/)[0];
  return code === "es" ? "es" : "en";
}

/** Translate one key, filling {name} placeholders. Unknown keys come back as the key. */
export function translate(lang: UiLang, key: I18nKey, vars?: Record<string, string | number>): string {
  const raw = TABLES[lang]?.[key] ?? EN[key] ?? String(key);
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (m, name) => (name in vars ? String(vars[name]) : m));
}

/** The learner's own language from a user document: the active language course first, then settings. */
export function nativeLangOfUser(user: any): UiLang {
  const s = user?.settings;
  const courses: any[] = s?.courses ?? [];
  const active = courses.find((c) => c?.courseId && c.courseId === s?.activeCourseId) ?? courses.find((c) => c?.kind === "language") ?? null;
  if (active && active.kind && active.kind !== "language") return "en";
  return uiLangOf(active?.language?.nativeLang || s?.nativeLang || s?.learning?.nativeLang);
}

export type Translator = ((key: I18nKey, vars?: Record<string, string | number>) => string) & { lang: UiLang };

export function makeTranslator(lang: UiLang): Translator {
  const fn = ((key: I18nKey, vars?: Record<string, string | number>) => translate(lang, key, vars)) as Translator;
  fn.lang = lang;
  return fn;
}

/** The translator for the signed-in learner (English for everyone but Spanish speakers learning English). */
export function useT(): Translator {
  const { user } = useUser() as any;
  const lang = nativeLangOfUser(user);
  return useMemo(() => makeTranslator(lang), [lang]);
}
